import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Button,
  Autocomplete,
  TextField,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Tooltip,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createFilterOptions } from "@mui/material/Autocomplete";

import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import axios from "axios";
import ETFLogo_NoTag from "../../assets/images/ETFLogo_NoTag.png";
import { supabaseDev } from "../../supabaseDevClient/supabaseDevClient";
import BASE_URL from "../../config/apiConfig";

const SECTION_LINKS = [
  {
    label: "ETF Hub",
    href: "/",
    description:
      "An overview of the Indian ETF Market, with visibility to ETF types & categories, top performing ETFs and Risk vs Return analyses.",
  },
  {
    label: "ETF Screener",
    href: "/screener",
    description: "Quickly search the ETF list based on your favourite filters",
  },
  {
    label: "ETF Research",
    href: "/funds/etf/SBI-ETF-Nifty-50",
    description:
      "A comprehensive study of an ETF's attributes and past performance.",
  },
  {
    label: "Compare ETFs",
    href: "/funds/etf/compare/SBI-ETF-Nifty-50-Vs-Nippon-India-ETF-Nifty-50-BeES",
    description:
      "Compare any two ETFs to understand how they differ in objectives, price trends, holdings overlap between the ETFs, performance metrics and liquidity metrics.",
  },
  {
    label: "Indices",
    href: "/indiceshub",
    description:
      "Get an overview of the index landscape in the Indian Market. You can see the available indices, top performers, risk-vs-reward chart and view the recently launched indices.",
  },
  {
    label: "Index Research",
    href: "/indices/NIFTY-AUTO",
    description: "Comprehensive study of an index in the Indian market.",
  },
  {
    label: "Stock Finder",
    href: "/stock-finder",
    description:
      "Find all ETFs that hold your favourite stock. Use this if you would like a certain exposure to a specific stock.",
  },
  // {
  //   label: "Report",
  //   href: "/report",
  //   description:
  //     "Generate and download a monthly PDF report of the previous month's ETF data, including tables and charts.",
  // },
];

const filterOptions = createFilterOptions({
  stringify: (option) => `${option.name} ${option.ticker || ""}`.toLowerCase(),
});

function isActive(link, pathname) {
  if (link.label === "ETF Hub")
    return pathname === "/" || pathname === "/etfhub";
  if (link.label === "Compare ETFs")
    return pathname.startsWith("/funds/etf/compare");
  if (link.label === "ETF Research")
    return (
      pathname.startsWith("/funds/etf/") &&
      !pathname.startsWith("/funds/etf/compare")
    );
  if (link.label === "Index Research")
    return (
      pathname.startsWith("/indices/") && !pathname.startsWith("/indiceshub")
    );
  if (link.label === "Stock Finder") return pathname === "/stock-finder";
  return pathname === link.href || pathname.startsWith(link.href + "/");
}

function formatMonthYear(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

function getInitials(nameOrEmail) {
  if (!nameOrEmail) return "?";
  const name = nameOrEmail.split("@")[0];
  return name
    .split(/[.\s_-]+/)
    .map((p) => p.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");
}

function NavLinks() {
  const { pathname } = useLocation();
  return (
    <Box
      component="ul"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "2px",
        listStyle: "none",
        m: 0,
        p: 0,
        border: "1px solid #e0e0e0",
        borderRadius: "10px",
        py: "8px",
        px: "16px",
        backgroundColor: "#fff",
        overflowX: "auto",
        overflowY: "hidden",
        "&::-webkit-scrollbar": { display: "none" },
        scrollbarWidth: "none",
      }}
    >
      {SECTION_LINKS.map((link) => {
        const active = isActive(link, pathname);
        return (
          <Box component="li" key={link.label} sx={{ flexShrink: 0 }}>
            <Tooltip
              title={link.description || ""}
              placement="bottom"
              arrow
              classes={{ popper: "custom-tooltip" }}
            >
              <span>
                <Typography
                  component={Link}
                  to={link.href}
                  data-label={link.label}
                  sx={{
                    // Column flex + a hidden bold "ghost" (::after) keeps every link
                    // sized for its active (Medium/600) width at all times. Toggling
                    // active then only animates color/background — the row never
                    // reflows, so there's no flicker/jump when switching links.
                    display: "inline-flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: "12px",
                    px: "16px",
                    "@media (min-width: 1500px)": { px: "24px" },
                    borderRadius: "7px",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                    fontSize: "14px",
                    lineHeight: 1,
                    fontFamily: active
                      ? "'Poppins-Medium'"
                      : "'Poppins-Regular'",
                    fontWeight: active ? 600 : 400,
                    color: active ? "#ffffff" : "#444",
                    backgroundColor: active ? "#173F3A" : "transparent",
                    transition: "color 0.15s ease, background-color 0.15s ease",
                    "&::after": {
                      content: "attr(data-label)",
                      height: 0,
                      overflow: "hidden",
                      visibility: "hidden",
                      pointerEvents: "none",
                      fontFamily: "'Poppins-Medium'",
                      fontWeight: 600,
                    },
                    "&:hover": {
                      color: active ? "#ffffff" : "#17332F",
                      backgroundColor: active
                        ? "#173F3A"
                        : "rgba(23,63,58,0.06)",
                    },
                  }}
                >
                  {link.label}
                </Typography>
              </span>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );
}

function MobileDrawer({ open, onClose, user, authLoading, navigate }) {
  const { pathname } = useLocation();

  const handleNav = (href) => {
    onClose();
    navigate(href);
  };

  const handleLogout = async () => {
    onClose();
    await supabaseDev.auth.signOut();
    window.location.href = "/signin";
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: 260 } } }}
    >
      {/* Drawer header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Link
          to="/"
          onClick={onClose}
          style={{ display: "flex", alignItems: "center" }}
        >
          <img
            src={ETFLogo_NoTag}
            alt="ETF India"
            style={{
              height: 36,
              width: "auto",
              marginLeft: "15px",
            }}
          />
        </Link>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Nav links */}
      <List disablePadding>
        {SECTION_LINKS.map((link) => {
          const active = isActive(link, pathname);
          return (
            <ListItem key={link.label} disablePadding>
              <ListItemButton
                onClick={() => handleNav(link.href)}
                sx={{
                  px: 2.5,
                  py: 1.25,
                  backgroundColor: active ? "#173F3A" : "transparent",
                  "&:hover": {
                    backgroundColor: active ? "#173F3A" : "rgba(23,63,58,0.06)",
                  },
                }}
              >
                <ListItemText
                  primary={link.label}
                  slotProps={{
                    primary: {
                      sx: {
                        fontSize: 14,
                        fontWeight: active ? 600 : 400,
                        color: active ? "#fff" : "#444",
                      },
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ my: 1 }} />

      {/* Auth section */}
      {!authLoading && (
        <Box sx={{ px: 2, pb: 2 }}>
          {user ? (
            <>
              <Typography
                variant="body2"
                sx={{ color: "#17332F", mb: 1, wordBreak: "break-all" }}
              >
                {user.email}
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  onClose();
                  navigate("/portfoliolist");
                }}
                sx={{
                  mb: 1,
                  textTransform: "none",
                  borderColor: "#17332F",
                  color: "#17332F",
                }}
              >
                Portfolio
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  onClose();
                  navigate("/watchlist");
                }}
                sx={{
                  mb: 1,
                  textTransform: "none",
                  borderColor: "#17332F",
                  color: "#17332F",
                }}
              >
                Watchlist
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleLogout}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#17332F",
                  color: "#fff",
                }}
              >
                Logout
              </Button>
            </>
          ) : /* Sign In option hidden for now
            <Button
              fullWidth
              variant="contained"
              component={Link}
              to="/signin"
              onClick={onClose}
              sx={{
                textTransform: "none",
                backgroundColor: "#17332F",
                color: "#fff",
              }}
            >
              Sign In
            </Button>
            */
          null}
        </Box>
      )}
    </Drawer>
  );
}

function HeaderBar({ noBorder = false }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [ufunds, setUfunds] = useState([]);
  const [searchValue, setSearchValue] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aumDate, setAumDate] = useState(null);
  const [monthEndDate, setMonthEndDate] = useState(null);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/etf-hub/market-snapshot`)
      .then((res) => {
        const snap = res.data?.results || {};
        setAumDate(snap?.aum_date || null);
        setMonthEndDate(snap?.month_end_date || snap?.ref_dt || null);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/ufunds`)
      .then((res) => {
        setUfunds(res.data?.results || []);
      })
      .catch((error) => {
        console.error("Error fetching UFunds:", error);
      });
  }, []);

  useEffect(() => {
    const { data: authListener } = supabaseDev.auth.onAuthStateChange(
      (_event, session) => {
        const u = session?.user ?? null;
        setUser(u);
        if (u?.id) localStorage.setItem("user_id", u.id);
        else localStorage.removeItem("user_id");
        setAuthLoading(false);
      }
    );
    return () => authListener.subscription.unsubscribe();
  }, []);

  const handleFundSelect = (_, fund) => {
    if (fund) {
      navigate(`/funds/etf/${fund.name.replace(/\s+/g, "-")}`);
      setSearchValue(null);
    }
  };

  const SearchBar = (
    <Autocomplete
      size="small"
      sx={{
        width: "100%",
        "@media (min-width: 1290px)": { width: 180 },
        "@media (min-width: 1500px)": { width: 240 },
        "& .MuiOutlinedInput-root": {
          borderRadius: "10px",
          fontSize: "14px",
        },
      }}
      slotProps={{
        popper: {
          sx: {
            "& .MuiAutocomplete-option": { fontSize: "12px" },
            "@media (min-width: 1290px)": { width: "280px !important" },
            "@media (min-width: 1500px)": { width: "360px !important" },
          },
        },
      }}
      options={ufunds}
      value={searchValue}
      onChange={handleFundSelect}
      getOptionLabel={(option) => option.dropdown_name || option.name || ""}
      getOptionKey={(option) => option.ticker || option.name}
      filterOptions={filterOptions}
      disablePortal
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search ETFs"
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <>
                  <SearchIcon
                    sx={{ fontSize: 18, color: "#94a3b8", mr: 0.5 }}
                  />
                  {params.InputProps.startAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );

  const DateInfo = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
      <Typography
        sx={{
          fontSize: "11px",
          color: "#64748b",
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        Data as of
      </Typography>
      <Tooltip
        title={
          <Box>
            <Typography variant="caption" display="block">
              Price Data as of end of{" "}
              <strong>{formatMonthYear(monthEndDate)}</strong>
            </Typography>
            <Typography variant="caption" display="block">
              Expense Ratio Data as of end of{" "}
              <strong>{formatMonthYear(monthEndDate)}</strong>
            </Typography>
            <Typography variant="caption" display="block">
              AUM Data as of end of <strong>{formatMonthYear(aumDate)}</strong>
            </Typography>
          </Box>
        }
        enterTouchDelay={0}
        leaveTouchDelay={4000}
        placement="bottom-end"
        arrow
        classes={{ popper: "custom-tooltip" }}
      >
        <InfoOutlinedIcon
          sx={{ fontSize: "13px", color: "#94a3b8", cursor: "pointer" }}
        />
      </Tooltip>
    </Box>
  );

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1100,
        backgroundColor: "#fff",
        borderBottom: "none",
      }}
    >
      <Container maxWidth={false} className="custom-container-index">
        {/* ── Mobile layout (up to 1280px) ── */}
        <Box
          sx={{
            display: "block",
            "@media (min-width: 1290px)": { display: "none" },
          }}
        >
          {/* Row 1: Logo + Hamburger */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minHeight: 56,
              py: 0.5,
            }}
          >
            <Link to="/" style={{ display: "flex", alignItems: "center" }}>
              <img
                src={ETFLogo_NoTag}
                alt="ETF India"
                style={{
                  height: 36,
                  width: "auto",
                  marginLeft: "15px",
                }}
              />
            </Link>

            <IconButton
              size="medium"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ color: "#17332F" }}
              aria-label="Open navigation menu"
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Row 2: As of Date + Search */}
          <Box sx={{ pb: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
              {DateInfo}
            </Box>
            {SearchBar}
          </Box>
        </Box>

        {/* ── Desktop layout (1280px+) ── */}
        <Box
          sx={{
            display: "none",
            "@media (min-width: 1290px)": { display: "flex" },
            alignItems: "center",
            minHeight: 64,
            py: 1,
            gap: 2,
          }}
        >
          {/* Logo */}
          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
            <Link to="/" style={{ display: "flex", alignItems: "center" }}>
              <img
                src={ETFLogo_NoTag}
                alt="ETF India"
                style={{
                  height: 36,
                  width: "auto",
                  marginLeft: "15px",
                }}
              />
            </Link>
          </Box>

          {/* Section nav — centered */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <NavLinks />
          </Box>

          {/* Search + Auth */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              flexShrink: 0,
              gap: 0.25,
            }}
          >
            <Box sx={{ alignSelf: "flex-end" }}>{DateInfo}</Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {SearchBar}

              {!authLoading && user ? (
                <>
                  <IconButton
                    size="small"
                    onClick={(e) => setProfileAnchor(e.currentTarget)}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "#17332F",
                        color: "#fff",
                        fontWeight: "bold",
                        width: 36,
                        height: 36,
                      }}
                    >
                      {getInitials(
                        user.user_metadata?.username ||
                          user.user_metadata?.full_name ||
                          user.email
                      )}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={profileAnchor}
                    open={Boolean(profileAnchor)}
                    onClose={() => setProfileAnchor(null)}
                    slotProps={{ paper: { sx: { mt: 1.5, minWidth: 150 } } }}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  >
                    <Box sx={{ px: 2, py: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "#17332F", wordBreak: "break-all" }}
                      >
                        {user.email}
                      </Typography>
                    </Box>
                    <MenuItem
                      onClick={() => {
                        setProfileAnchor(null);
                        navigate("/portfoliolist");
                      }}
                    >
                      Portfolio
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setProfileAnchor(null);
                        navigate("/watchlist");
                      }}
                    >
                      Watchlist
                    </MenuItem>
                    <MenuItem
                      onClick={async () => {
                        setProfileAnchor(null);
                        await supabaseDev.auth.signOut();
                        window.location.href = "/signin";
                      }}
                    >
                      Logout
                    </MenuItem>
                  </Menu>
                </>
              ) : !authLoading /* Sign In option hidden for now
              <Button
                component={Link}
                to="/signin"
                variant="contained"
                sx={{
                  backgroundColor: "#17332F",
                  color: "#fff",
                  textTransform: "none",
                  height: 36,
                  minWidth: 80,
                  fontSize: 14,
                }}
              >
                Sign In
              </Button>
              */ ? null : null}
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Mobile Drawer */}
      <MobileDrawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        user={user}
        authLoading={authLoading}
        navigate={navigate}
      />
    </Box>
  );
}

export default function SectionNavbar({
  inline = false,
  header = false,
  noBorder = false,
}) {
  if (header) return <HeaderBar noBorder={noBorder} />;
  if (inline) return <NavLinks />;

  return (
    <Box sx={{ backgroundColor: "#ffffff", pt: 0.75, pb: 0, mt: 1 }}>
      <Container
        maxWidth={false}
        className="custom-container-index"
        sx={{ px: { xs: 2, md: 4 } }}
      >
        <NavLinks />
      </Container>
    </Box>
  );
}
